package com.siriusxm.pia.views.unifiedaggregator.types

import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlin.math.max

internal fun buildIssueHighlightContext(
    rawJson: String,
    issues: List<ValidationIssue>
): IssueHighlightContext? {
    if (rawJson.isBlank()) {
        return null
    }
    return try {
        val element = smithyValidationJson.parseToJsonElement(rawJson)
        val formatted = PrettyJsonFormatter.build(element, escapeControlChars = true)
        val highlights = mutableMapOf<String, LineHighlight>()
        issues.forEach { issue ->
            val pointer = issue.pointer ?: return@forEach
            val pointerKey = pointer.toPointerKey() ?: return@forEach
            if (pointerKey == "/") {
                return@forEach
            }
            val range = formatted.pointerRanges.findRange(pointerKey) ?: return@forEach
            if (range.end <= range.start) {
                return@forEach
            }
            val startLine = formatted.lineFor(range.start)
            val endLine = formatted.lineFor(max(range.end - 1, range.start))
            highlights[pointer] = LineHighlight(startLine + 1, endLine + 1)
        }
        IssueHighlightContext(rawJson, formatted, highlights)
    } catch (_: Throwable) {
        null
    }
}

internal data class IssueHighlightContext(
    val rawJson: String,
    val formattedJson: FormattedJson,
    val lineHighlights: Map<String, LineHighlight>
)

data class LineHighlight(val startLine: Int, val endLine: Int)

internal fun LineHighlight.label(): String {
    return if (startLine == endLine) {
        "line $startLine"
    } else {
        "lines $startLine-$endLine"
    }
}

internal object PrettyJsonFormatter {
    private const val INDENT = "    "

    fun build(element: JsonElement, escapeControlChars: Boolean = false): FormattedJson {
        val builder = Builder(escapeControlChars)
        builder.appendElement(element, JsonPointerPath.root, 0)
        val rendered = builder.output.toString()
        return FormattedJson(
            rendered,
            builder.pointerRanges.toMap(),
            computeLineOffsets(rendered)
        )
    }

    private class Builder(
        private val escapeControlChars: Boolean
    ) {
        val output = StringBuilder()
        val pointerRanges = mutableMapOf<String, TextRange>()

        fun appendElement(element: JsonElement, pointer: JsonPointerPath, level: Int) {
            val start = output.length
            when (element) {
                is JsonObject -> appendObject(element, pointer, level)
                is JsonArray -> appendArray(element, pointer, level)
                is JsonPrimitive -> appendPrimitive(element)
            }
            pointerRanges[pointer.asKey()] = TextRange(start, output.length)
        }

        private fun appendObject(obj: JsonObject, pointer: JsonPointerPath, level: Int) {
            output.append('{')
            if (obj.isEmpty()) {
                output.append('}')
                return
            }
            output.append('\n')
            val entries = obj.entries.toList()
            entries.forEachIndexed { index, entry ->
                appendIndent(level + 1)
                appendQuoted(entry.key)
                output.append(':')
                appendElement(entry.value, pointer.child(entry.key), level + 1)
                if (index < entries.lastIndex) {
                    output.append(',')
                }
                output.append('\n')
            }
            appendIndent(level)
            output.append('}')
        }

        private fun appendArray(array: JsonArray, pointer: JsonPointerPath, level: Int) {
            output.append('[')
            if (array.isEmpty()) {
                output.append(']')
                return
            }
            output.append('\n')
            array.forEachIndexed { index, child ->
                appendIndent(level + 1)
                appendElement(child, pointer.child(index.toString()), level + 1)
                if (index < array.lastIndex) {
                    output.append(',')
                }
                output.append('\n')
            }
            appendIndent(level)
            output.append(']')
        }

        private fun appendIndent(level: Int) {
            repeat(level) { output.append(INDENT) }
        }

        private fun appendPrimitive(primitive: JsonPrimitive) {
            if (primitive.isString && escapeControlChars) {
                appendQuoted(primitive.content)
            } else {
                output.append(primitive.toString())
            }
        }

        private fun appendQuoted(value: String) {
            output.append('"')
            value.forEach { ch ->
                when (ch) {
                    '\\' -> output.append("\\\\")
                    '"' -> output.append("\\\"")
                    '\n' -> output.append("\\n")
                    '\r' -> output.append("\\r")
                    '\t' -> output.append("\\t")
                    '\b' -> output.append("\\b")
                    '\u000C' -> output.append("\\f")
                    '\u2028', '\u2029' -> appendUnicodeEscape(ch)
                    else -> if (ch.code < 0x20) {
                        appendUnicodeEscape(ch)
                    } else {
                        output.append(ch)
                    }
                }
            }
            output.append('"')
        }

        private fun appendUnicodeEscape(ch: Char) {
            output.append("\\u")
            output.append(ch.code.toString(16).padStart(4, '0'))
        }
    }
}

internal data class FormattedJson(
    val text: String,
    val pointerRanges: Map<String, TextRange>,
    private val lineOffsets: IntArray
) {
    fun lineFor(offset: Int): Int {
        val safeOffset = offset.coerceIn(0, text.length)
        val idx = lineOffsets.binarySearchIndex(safeOffset)
        return if (idx >= 0) {
            idx
        } else {
            val insertion = -(idx + 1)
            (insertion - 1).coerceAtLeast(0)
        }
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is FormattedJson) return false
        if (text != other.text) return false
        if (pointerRanges != other.pointerRanges) return false
        if (!lineOffsets.contentEquals(other.lineOffsets)) return false
        return true
    }

    override fun hashCode(): Int {
        var result = text.hashCode()
        result = 31 * result + pointerRanges.hashCode()
        result = 31 * result + lineOffsets.contentHashCode()
        return result
    }
}

internal data class TextRange(val start: Int, val end: Int)

private fun Map<String, TextRange>.findRange(pointerKey: String): TextRange? {
    var key: String? = pointerKey
    while (key != null) {
        if (key == "/" && pointerKey != "/") {
            break
        }
        this[key]?.let { return it }
        key = key.parentPointerKey()
    }
    val tail = pointerKey.substringAfterLast('/', missingDelimiterValue = "")
        .takeIf { it.isNotBlank() }
    if (tail != null) {
        return entries
            .asSequence()
            .filter { (candidateKey, _) -> candidateKey != "/" && candidateKey.endsWith("/${tail}") }
            .map { it.value }
            .firstOrNull()
    }
    return null
}

private fun String.parentPointerKey(): String? {
    if (this.isBlank() || this == "/") {
        return null
    }
    val idx = this.lastIndexOf('/')
    if (idx <= 0) {
        return "/"
    }
    return this.substring(0, idx)
}

private fun computeLineOffsets(text: String): IntArray {
    if (text.isEmpty()) {
        return intArrayOf(0)
    }
    val offsets = mutableListOf(0)
    text.forEachIndexed { index, ch ->
        if (ch == '\n') {
            offsets += index + 1
        }
    }
    return offsets.toIntArray()
}

private fun IntArray.binarySearchIndex(value: Int): Int {
    var low = 0
    var high = size - 1
    while (low <= high) {
        val mid = (low + high).ushr(1)
        val midValue = this[mid]
        when {
            midValue < value -> low = mid + 1
            midValue > value -> high = mid - 1
            else -> return mid
        }
    }
    return -(low + 1)
}

internal data class JsonPointerPath(val segments: List<String>) {
    fun child(segment: String): JsonPointerPath = JsonPointerPath(segments + segment)

    fun asKey(): String {
        if (segments.isEmpty()) {
            return "/"
        }
        return segments.joinToString(separator = "/", prefix = "/") { part ->
            part.escapePointerSegment()
        }
    }

    companion object {
        val root = JsonPointerPath(emptyList())
    }
}

internal fun String.toPointerKey(): String? {
    if (this.isBlank()) {
        return "/"
    }
    if (this == "$" || this == "$.") {
        return "/"
    }
    if (this.startsWith('/')) {
        return this
    }
    if (!this.startsWith('$')) {
        return null
    }
    val trimmed = this.removePrefix("$").trimStart('.')
    if (trimmed.isEmpty()) {
        return "/"
    }
    val segments = mutableListOf<String>()
    val current = StringBuilder()
    var index = 0
    while (index < trimmed.length) {
        when (val ch = trimmed[index]) {
            '.' -> {
                if (current.isNotEmpty()) {
                    segments += current.toString()
                    current.clear()
                }
            }

            '[' -> {
                if (current.isNotEmpty()) {
                    segments += current.toString()
                    current.clear()
                }
                val closing = trimmed.indexOf(']', index + 1)
                if (closing == -1) {
                    return null
                }
                val indexSegment = trimmed.substring(index + 1, closing)
                val cleanedSegment = indexSegment
                    .trim()
                    .let { segment ->
                        segment.trim('"').trim('\'')
                    }
                if (cleanedSegment.isNotEmpty()) {
                    segments += cleanedSegment
                }
                index = closing
            }

            ']' -> {}
            else -> current.append(ch)
        }
        index++
    }
    if (current.isNotEmpty()) {
        segments += current.toString()
    }
    if (segments.isEmpty()) {
        return "/"
    }
    return segments.joinToString(separator = "/", prefix = "/") { segment ->
        segment.escapePointerSegment()
    }
}

internal fun String.escapePointerSegment(): String {
    return this
        .replace("~", "~0")
        .replace("/", "~1")
}

internal data class FormattingAttempt(val prettyJson: String?, val error: String?)

internal fun String.tryFormatJson(): FormattingAttempt {
    if (isBlank()) {
        return FormattingAttempt(null, "JSON payload is required")
    }
    return try {
        val element = smithyValidationJson.parseToJsonElement(this)
        val formatted = PrettyJsonFormatter.build(element, escapeControlChars = false).text
        FormattingAttempt(formatted, null)
    } catch (t: Throwable) {
        FormattingAttempt(null, t.message ?: "Unable to parse the provided JSON string.")
    }
}

internal fun String.formatIfPossible(): String {
    if (isBlank()) {
        return this
    }
    return try {
        val element = smithyValidationJson.parseToJsonElement(this)
        PrettyJsonFormatter.build(element, escapeControlChars = false).text
    } catch (_: Throwable) {
        this
    }
}
